import { motion } from "framer-motion";

const draw = { initial: { pathLength: 0, opacity: 0 }, whileInView: { pathLength: 1, opacity: 1 }, viewport: { once: true, margin: "-15% 0px" }, transition: { duration: 1.6, ease: [0.4, 0, 0.2, 1] } };

export const AreaSpark = ({ series, color = "#1E88F7", height = 200, id = "as", dark = true }) => {
    const w = 560;
    const max = Math.max(...series);
    const min = Math.min(...series);
    const pts = series.map((v, i) => [ (i / (series.length - 1)) * w, height - 16 - ((v - min) / (max - min || 1)) * (height - 40) ]);
    const d = pts.map((p, i) => (i === 0 ? `M ${p[0]},${p[1]}` : `L ${p[0]},${p[1]}`)).join(" ");
    return (
        <svg viewBox={`0 0 ${w} ${height}`} className="w-full" role="img" aria-label="Trend chart, illustrative data">
            <defs>
                <linearGradient id={`${id}-g`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor={color} stopOpacity="0.28" />
                    <stop offset="1" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            {[0.25, 0.5, 0.75].map((f) => (
                <line key={f} x1="0" x2={w} y1={height * f} y2={height * f} stroke={dark ? "rgba(247,248,246,0.08)" : "rgba(11,18,32,0.08)"} strokeDasharray="2 6" />
            ))}
            <motion.path d={`${d} L ${w},${height} L 0,${height} Z`} fill={`url(#${id}-g)`} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1.4, delay: 0.4 }} />
            <motion.path d={d} fill="none" stroke={color} strokeWidth="2" {...draw} />
            <motion.circle cx={pts[pts.length - 1][0] - 2} cy={pts[pts.length - 1][1]} r="4" fill={color} initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 1.5 }} />
        </svg>
    );
};

export const Bars = ({ items, color = "#1E88F7", dark = true, height = 150 }) => {
    const max = Math.max(...items.map((i) => i.v));
    return (
        <div className="flex items-end gap-2.5" style={{ height }} role="img" aria-label="Bar chart, illustrative data">
            {items.map((it, i) => (
                <div key={i} className="group relative flex h-full flex-1 flex-col justify-end gap-2">
                    <motion.div
                        className="w-full rounded-t-[3px]"
                        style={{ background: it.c || color, opacity: 0.9 }}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${(it.v / max) * 100}%` }}
                        viewport={{ once: true, margin: "-15% 0px" }}
                        transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    />
                    <span className={`truncate font-mono text-[9px] uppercase tracking-wider ${dark ? "text-cloud" : "text-slategray"}`}>{it.n}</span>
                </div>
            ))}
        </div>
    );
};

export const Donut = ({ pct, label, sub, color = "#1E88F7", size = 130 }) => {
    const r = 54;
    const c = 2 * Math.PI * r;
    return (
        <div className="flex items-center gap-5">
            <svg width={size} height={size} viewBox="0 0 130 130" role="img" aria-label={`${label} donut chart`}>
                <circle cx="65" cy="65" r={r} fill="none" stroke="rgba(247,248,246,0.08)" strokeWidth="9" />
                <motion.circle
                    cx="65" cy="65" r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
                    strokeDasharray={c} initial={{ strokeDashoffset: c }} whileInView={{ strokeDashoffset: c * (1 - pct / 100) }}
                    viewport={{ once: true, margin: "-15% 0px" }} transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                    transform="rotate(-90 65 65)"
                />
                <text x="65" y="70" textAnchor="middle" className="fill-ivory font-display font-extrabold" fontSize="24">{pct}%</text>
            </svg>
            <div>
                <p className="font-display text-base font-bold text-ivory">{label}</p>
                {sub && <p className="mt-1 max-w-[180px] text-sm text-cloud">{sub}</p>}
            </div>
        </div>
    );
};

export const Waterfall = ({ steps, dark = true, height = 190 }) => {
    const w = 560;
    const n = steps.length;
    const bw = w / n - 28;
    let cum = 0;
    const bars = steps.map((s, i) => {
        const from = i === steps.length - 1 ? 0 : cum;
        const to = i === steps.length - 1 ? cum : cum + s.delta;
        cum += s.delta;
        return { ...s, from, to };
    });
    const max = Math.max(...bars.map((b) => Math.max(b.from, b.to)));
    const y = (v) => height - 26 - (v / max) * (height - 56);
    return (
        <svg viewBox={`0 0 ${w} ${height}`} className="w-full" role="img" aria-label="Waterfall chart, illustrative data">
            {bars.map((b, i) => {
                const x = i * (w / n) + 14;
                const top = y(Math.max(b.from, b.to));
                const h = Math.max(3, Math.abs(y(b.from) - y(b.to)));
                const last = i === n - 1;
                return (
                    <g key={i}>
                        <motion.rect x={x} width={bw} initial={{ height: 0, y: height - 26 }} whileInView={{ height: h, y: top }} viewport={{ once: true, margin: "-15% 0px" }} transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }} fill={last ? "#1E88F7" : b.delta < 0 ? "rgba(225,29,72,0.55)" : "rgba(57,119,246,0.75)"} rx="2" />
                        <text x={x + bw / 2} y={height - 8} textAnchor="middle" fontSize="9" className={`font-mono uppercase ${dark ? "fill-cloud" : "fill-slategray"}`}>{b.n}</text>
                    </g>
                );
            })}
        </svg>
    );
};

export const FunnelV = ({ steps, dark = true }) => {
    const max = steps[0].v;
    return (
        <div className="space-y-2.5" role="img" aria-label="Funnel chart, illustrative data">
            {steps.map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                    <motion.div
                        className="flex h-9 items-center justify-end rounded-r-[3px] pr-3"
                        style={{ background: s.c || (dark ? "rgba(247,248,246,0.1)" : "rgba(11,18,32,0.08)"), borderLeft: "2px solid #1E88F7" }}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${Math.max(22, (s.v / max) * 78)}%` }}
                        viewport={{ once: true, margin: "-15% 0px" }}
                        transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <span className="whitespace-nowrap font-mono text-[10px] text-ivory">{s.v.toLocaleString()}</span>
                    </motion.div>
                    <span className={`font-mono text-[10px] uppercase tracking-wider ${dark ? "text-cloud" : "text-slategray"}`}>{s.n}</span>
                </div>
            ))}
        </div>
    );
};